import { nanoid } from 'nanoid';
import { useTranslation } from 'react-i18next';

import { LinkStyle } from '@sola/ark-core/atoms/link-style/link-style.tsx';
import { Link } from '@sola/ark-core/atoms/link/link.tsx';
import i18n from '@sola/locales/index.ts';
import { AuthScenario } from '@sola/schema/dotcom/auth.ts';
import type { GraphQLSupporterPaymentMethod } from '@sola/schema/dotcom/supporters/schema.generated.ts';

import type {
  LocalAlertConfig,
  LocalAlertCreateProps,
} from '../types/localeAlerts.ts';

const ContactSupportAction = () => {
  const { t } = useTranslation('my');
  return (
    <LinkStyle inline noUnderline>
      <Link href={t('ctas.contactSupport.url')}>
        {t('ctas.contactSupport.text')}
      </Link>
    </LinkStyle>
  );
};

/**
 * Function for creating local alert data with an id.
 */
export const createLocalAlert = (
  alertProps: LocalAlertCreateProps,
): LocalAlertConfig => {
  return { id: nanoid(), ...alertProps };
};

export const createPaymentMethodSuccessAlert = (
  { creditCard, ach }: GraphQLSupporterPaymentMethod,
  actionTranslationKey: string,
): LocalAlertCreateProps => {
  const { t } = i18n;
  let lastFour = creditCard?.lastFour || '';
  let type = creditCard?.cardType || '';

  if (ach) {
    lastFour = ach.lastFourOfAccountNumber || '';
    type = t('my:payments.methodTypes.ach.short');
  }

  return {
    translationKey: 'payments.success',
    variant: 'positive',
    translationData: {
      lastFour,
      type,
      action: t(`my:${actionTranslationKey}`),
    },
  };
};

export const createPaymentMethodPartialSuccessAlert =
  (): LocalAlertCreateProps => {
    return {
      translationKey: 'payments.partialSuccess',
      variant: 'warning',
      dismissible: false,
      action: <ContactSupportAction />,
    };
  };

export const removePaymentMethodPartialSuccessAlert =
  (): LocalAlertCreateProps => {
    return {
      translationKey: 'payments.remove.partialSuccess',
      variant: 'negative',
      dismissible: true,
      action: <ContactSupportAction />,
    };
  };

export const removePaymentMethodFailureAlert = (): LocalAlertCreateProps => {
  return {
    translationKey: 'payments.remove.failure',
    variant: 'negative',
    dismissible: true,
    action: <ContactSupportAction />,
  };
};

export const createPaymentPlanSuccessAlert = (): LocalAlertCreateProps => {
  return {
    translationKey: 'commitments.detailsSheet.paymentPlanSuccess',
    variant: 'positive',
    closable: true,
  };
};

export const createSupportAlert = (translationKey: string) => {
  return createLocalAlert({
    translationKey,
    variant: 'negative',
    dismissible: true,
    action: <ContactSupportAction />,
  });
};

export const createCommitmentPaymentAddErrorAlert =
  (): LocalAlertCreateProps => {
    return {
      translationKey: 'makeAPayment.error',
      variant: 'negative',
      dismissible: true,
      action: <ContactSupportAction />,
    };
  };

export const createPartialDataAlert = (
  dataKey: string,
): LocalAlertCreateProps => {
  return {
    translationKey: `errors.partialData.${dataKey}`,
    variant: 'warning',
    action: <ContactSupportAction />,
  };
};

export const createPartialDataAlertMulti = (): LocalAlertCreateProps => {
  return {
    translationKey: `errors.partialData.base`,
    variant: 'warning',
    action: <ContactSupportAction />,
  };
};

export const createAddressUpdateSuccessAlert = (): LocalAlertCreateProps => ({
  translationKey: 'my-settings:addressUpdate.success',
  variant: 'positive',
  closable: true,
});

export const createAddressUpdateErrorAlert = (): LocalAlertCreateProps => {
  return {
    translationKey: 'my-settings:addressUpdate.error',
    variant: 'negative',
    closable: true,
    action: <ContactSupportAction />,
  };
};

export const createContactInfoUpdateSuccessAlert =
  (): LocalAlertCreateProps => ({
    translationKey: 'my-settings:contactInfoUpdate.success',
    variant: 'positive',
    closable: true,
  });

export const createContactInfoUpdateErrorAlert = (): LocalAlertCreateProps => {
  return {
    translationKey: 'my-settings:contactInfoUpdate.error',
    variant: 'negative',
    closable: true,
    action: <ContactSupportAction />,
  };
};

export const createUsernameUpdateSuccessAlert = (): LocalAlertCreateProps => ({
  translationKey: 'my-settings:usernameUpdate.success',
  variant: 'positive',
  closable: true,
});

export const createUsernameUpdateErrorAlert = (
  errorType?: string,
): LocalAlertCreateProps => {
  const errorKeyMap: Record<string, string> = {
    [AuthScenario.USERNAME_ALREADY_IN_USE]:
      'my-settings:usernameUpdate.errors.alreadyInUse',
    [AuthScenario.TOO_MANY_ATTEMPTS]:
      'my-settings:usernameUpdate.errors.tooManyAttempts',
    [AuthScenario.INTERNAL_ERROR]:
      'my-settings:usernameUpdate.errors.internalError',
  };

  const translationKey =
    (errorType && errorKeyMap[errorType]) || 'my-settings:errors.generic';

  return {
    translationKey,
    variant: 'negative',
    closable: true,
  };
};

export const createPasswordUpdateSuccessAlert = (): LocalAlertCreateProps => ({
  translationKey: 'my-settings:passwordUpdate.success',
  variant: 'positive',
  closable: true,
});

export const createPasswordUpdateErrorAlert = (): LocalAlertCreateProps => {
  return {
    translationKey: 'my-settings:passwordUpdate.error',
    variant: 'negative',
    closable: true,
    action: <ContactSupportAction />,
  };
};
